import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import type { TRootState } from 'slices/store';
import { createAppAsyncThunk } from 'slices/utils';
import type {
  IDefaultAPIResult,
  IDefaultAPIState,
} from 'types/state.interface';
import type { WithOptional } from 'types/utils.type';
import {
  addEntityToState,
  deleteEntityFromState,
  finishedLoadingFailure,
  finishedLoadingSuccess,
  isLoadingRequest,
  prepEntityForAPICall,
  stateHasFetchedAllObjectsSuccess,
  upsertEntity,
} from 'utils/sliceHelpers';
import { type TRole, createRole as create } from '@timeedit/types';
import api from '../services/api.service';

export const initialState: IDefaultAPIState<TRole, number> = {
  loading: false,
  hasErrors: false,
  results: [],
  map: {},
  page: 0,
  limit: 10000,
  totalPages: 0,
};

// Slice
const slice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    fetchRolesRequest: (state) => {
      isLoadingRequest(state);
    },
    fetchRolesSuccess: (
      state,
      {
        payload,
      }: PayloadAction<
        IDefaultAPIResult<WithOptional<TRole, 'rolePermissions'>>
      >
    ) => {
      stateHasFetchedAllObjectsSuccess<
        WithOptional<TRole, 'rolePermissions'>,
        TRole
      >(payload, state, create);
      finishedLoadingSuccess(state);
    },
    fetchRolesFailure: (state) => {
      finishedLoadingFailure(state);
    },
    createRole: (state) => {
      addEntityToState(state, { id: 0, name: 'New role' }, create);
      finishedLoadingSuccess(state);
    },
    saveRoleRequest: (state) => {
      isLoadingRequest(state);
    },
    saveRoleSuccess: (
      state,
      { payload }: PayloadAction<WithOptional<TRole, 'rolePermissions'>>
    ) => {
      upsertEntity<WithOptional<TRole, 'rolePermissions'>, TRole>(
        state,
        payload,
        create
      );
      finishedLoadingSuccess(state);
    },
    saveRoleFailure: (state) => {
      finishedLoadingFailure(state);
    },
    deleteRoleRequest: (state) => {
      isLoadingRequest(state);
    },
    deleteRoleSuccess: (state, { payload: roleId }: PayloadAction<number>) => {
      deleteEntityFromState(roleId, state);
      finishedLoadingSuccess(state);
    },
    deleteRoleFailure: (state) => {
      finishedLoadingFailure(state);
    },
  },
});

export default slice.reducer;

// Selectors
export const rolesLoading = (state: TRootState) => state.roles.loading;
export const rolesSelector = (state: TRootState) => state.roles.results;
export const roleSelector =
  (id?: number) =>
  (state: TRootState): TRole | undefined =>
    id !== undefined ? state.roles.map[id] : undefined;
export const rolesMapSelector = (state: TRootState) => state.roles.map;

// Actions
export const {
  fetchRolesRequest,
  fetchRolesSuccess,
  fetchRolesFailure,
  createRole,
  deleteRoleRequest,
  deleteRoleFailure,
  deleteRoleSuccess,
  saveRoleRequest,
  saveRoleSuccess,
  saveRoleFailure,
} = slice.actions;

export const fetchRoles = createAppAsyncThunk(
  'roles/fetchRoles',
  async (_, { dispatch }) => {
    try {
      dispatch(fetchRolesRequest());
      const result = await api.get({
        endpoint: `/roles`,
      });
      dispatch(fetchRolesSuccess(result));
    } catch (e) {
      dispatch(fetchRolesFailure());
      console.error(e);
    }
  }
);

export const saveRole = createAppAsyncThunk<void, TRole>(
  'roles/saveRole',
  async (role, { dispatch }) => {
    try {
      dispatch(saveRoleRequest());
      /**
       * Could be either create new or update
       * Depending on which, we need to use different methods (POST or PATCH)
       */
      if (role.id === 0) {
        // Create a new one
        const preppedRole = prepEntityForAPICall(role, true, true);
        const savedRole = await api.post({
          endpoint: `/roles`,
          data: preppedRole,
        });
        dispatch(saveRoleSuccess(savedRole));
        dispatch(deleteRoleSuccess(0));
      } else {
        const preppedRole = prepEntityForAPICall(role, true, true);
        const updatedRole = await api.patch({
          endpoint: `/roles/${role.id}`,
          data: preppedRole,
        });
        dispatch(saveRoleSuccess(updatedRole));
      }
    } catch (error) {
      dispatch(saveRoleFailure());
      console.error(error);
    }
  }
);

export const copyRole = createAppAsyncThunk<void, number>(
  'roles/copyRole',
  async (roleId, { dispatch, getState }) => {
    try {
      const roleToCopy = getState().roles.map[roleId];
      if (!roleToCopy) return;

      dispatch(saveRoleRequest());
      const preppedRole = prepEntityForAPICall(roleToCopy, true, true);
      const copiedRole = await api.post({
        endpoint: `/roles`,
        data: { ...preppedRole, name: `[COPY] ${preppedRole.name}` },
      });
      dispatch(saveRoleSuccess(copiedRole));
    } catch (e) {
      dispatch(saveRoleFailure());
      console.error(e);
    }
  }
);

export const deleteRole = createAppAsyncThunk<void, number>(
  'roles/deleteRole',
  async (roleId, { dispatch }) => {
    try {
      dispatch(deleteRoleRequest());
      /**
       * No need to send the request to the backend if the entity hasn't been saved
       */
      if (roleId !== 0)
        await api.delete({
          endpoint: `/roles/${roleId}`,
        });
      dispatch(deleteRoleSuccess(roleId));
    } catch (e) {
      dispatch(deleteRoleFailure());
      console.error(e);
    }
  }
);
